import { PANEL_EVENTS } from './protocol';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePanelLabel } from '../primitives/labels';
import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from '../primitives/Tooltip';
import { PanelGroup, TabConfig, FloatingWindow } from './types';
import { PanelLeft, Pin, PinOff } from '../primitives/icons';
import { generateUniqueId } from './utils';
import { globalDragState } from './globalDragState';
import '../theme/minimized-sidepanel.css';
function getCssVarPx(name: string, fallback: number): number {
    if (typeof window !== 'undefined') {
        const val = getComputedStyle(document.documentElement).getPropertyValue(
            name,
        );
        if (val) {
            const num = parseFloat(val);
            if (!isNaN(num)) return num;
        }
    }
    return fallback;
}
const ICON_ORDER_KEY = 'minimizedSidePanel.iconOrder';
const PINNED_GLOBAL_KEY = 'minimizedSidePanel.pinnedGlobal';
const POPUP_GEOMETRY_KEY = 'minimizedSidePanel.popupGeometry';

/** Remembered popup geometry per `${groupId}::${tabId}`. Size is restored on
 * every reopen; the icon distances make the popup reappear exactly as it was
 * left after a resize — offsetY = icon top − popup top (vertical rails),
 * offsetX = icon center − popup left (horizontal rails). `vertical` records
 * the rail axis the distances were measured on: a geometry read on a rail of
 * the other axis keeps its size but falls back to default anchoring. */
interface PopupGeometry {
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
    vertical: boolean;
}

const POPUP_MIN_WIDTH = 220;
const POPUP_MIN_HEIGHT = 80;
const POPUP_VIEWPORT_MARGIN = 8;

const isValidPopupGeometry = (value: unknown): value is PopupGeometry => {
    if (!value || typeof value !== 'object') return false;
    const entry = value as Record<string, unknown>;
    return (
        typeof entry.width === 'number' &&
        entry.width >= POPUP_MIN_WIDTH &&
        typeof entry.height === 'number' &&
        entry.height >= POPUP_MIN_HEIGHT &&
        typeof entry.offsetX === 'number' &&
        Number.isFinite(entry.offsetX) &&
        typeof entry.offsetY === 'number' &&
        Number.isFinite(entry.offsetY) &&
        typeof entry.vertical === 'boolean'
    );
};

const loadPopupGeometryMap = (): Record<string, PopupGeometry> => {
    try {
        const stored = localStorage.getItem(POPUP_GEOMETRY_KEY);
        if (!stored) return {};
        const parsed = JSON.parse(stored) as Record<string, unknown>;
        const map: Record<string, PopupGeometry> = {};
        Object.keys(parsed).forEach((key) => {
            const value = parsed[key];
            if (isValidPopupGeometry(value)) {
                map[key] = value;
            }
        });
        return map;
    } catch {
        return {};
    }
};

/** Merge one entry into storage with a fresh read: several minimized shells
 * (main column, timeline dock) share the key and must not clobber each
 * other's tabs. Returns the merged map for the caller's ref. */
const savePopupGeometryEntry = (
    key: string,
    entry: PopupGeometry,
): Record<string, PopupGeometry> => {
    const map = loadPopupGeometryMap();
    map[key] = entry;
    try {
        localStorage.setItem(POPUP_GEOMETRY_KEY, JSON.stringify(map));
    } catch {}
    return map;
};
interface MinimizedSidePanelProps {
    visibleGroups: PanelGroup[];
    popupTab: { tabId: string; groupId: string } | null;
    setPopupTab: React.Dispatch<
        React.SetStateAction<{ tabId: string; groupId: string } | null>
    >;
    setPopupPosition: React.Dispatch<
        React.SetStateAction<{ left: number; top: number } | null>
    >;
    popupPosition: { left: number; top: number } | null;
    popupWidth: number;
    setPopupWidth: React.Dispatch<React.SetStateAction<number>>;
    popupHeights: Record<string, number>;
    setPopupHeights: React.Dispatch<
        React.SetStateAction<Record<string, number>>
    >;
    popupResizeHandle: string | null;
    setPopupResizeHandle: React.Dispatch<React.SetStateAction<string | null>>;
    panelRef: React.RefObject<HTMLDivElement>;
    onFloatingWindowDrop?: (tab: any) => void;
    onTabDragStart?: (tab: TabConfig, groupId: string) => void;
    isDraggingPanel?: boolean;
    dragOffset?: { x: number; y: number } | null;
    dragPanelSize?: { width: number; height: number } | null;
    dragPos?: { x: number; y: number } | null;
    snapTarget?: 'left' | 'right' | 'top' | 'bottom' | null;
    panelPosition?: 'left' | 'right' | 'top' | 'bottom';
    setFloatingWindows?: React.Dispatch<React.SetStateAction<FloatingWindow[]>>;
    floatWindowMinWidth?: number;
    floatWindowMinHeight?: number;
    setPanelGroups?: React.Dispatch<React.SetStateAction<PanelGroup[]>>;
    setIsDraggingOutside?: React.Dispatch<React.SetStateAction<boolean>>;
    onMoveTab?: (params: {
        sourceGroupId: string;
        sourceTabId: string;
        targetGroupId: string;
        targetIndex: number;
    }) => void;
    onAddTabToMinimizedGroup?: (params: {
        tab: TabConfig;
        groupId?: string;
        position?: number;
    }) => void;
    onPanelLongPressMouseDown?: (e: React.MouseEvent) => void;
    panelId: string;
    handleTabDrop?: (
        e: DragEvent | React.DragEvent,
        targetTabId: string,
        targetGroupId: string,
        position: 'left' | 'right',
        setTabDropPosition: React.Dispatch<
            React.SetStateAction<Record<string, 'left' | 'right' | null>>
        >,
        setFloatingWindowsExternal?: React.Dispatch<
            React.SetStateAction<FloatingWindow[]>
        >,
    ) => void;
    handleDropOnZone?: (
        e: DragEvent | React.DragEvent,
        targetGroupId: string,
        position: 'before' | 'after',
    ) => void;
    setTabDropPosition?: React.Dispatch<
        React.SetStateAction<Record<string, 'left' | 'right' | null>>
    >;
    setFloatingWindowsExternal?: React.Dispatch<
        React.SetStateAction<FloatingWindow[]>
    >;
    disableTransitions?: boolean;
    panelDragGhostHtml?: string | null;
    setPanelDragGhostHtml?: (html: string | null) => void;
    snapOverlay?: React.ReactNode;
    onEmpty?: () => void;
    /** Icon alignment for the icon rail (start/center/end per the store) */
    iconAlignment?: 'start' | 'center' | 'end';
}
export const MinimizedSidePanel: React.FC<MinimizedSidePanelProps> = ({
    visibleGroups,
    popupTab,
    setPopupTab,
    setPopupPosition,
    popupPosition,
    popupWidth,
    setPopupWidth,
    popupHeights,
    setPopupHeights,
    popupResizeHandle,
    setPopupResizeHandle,
    panelRef,
    onFloatingWindowDrop,
    onTabDragStart,
    isDraggingPanel,
    dragOffset,
    dragPanelSize,
    dragPos,
    snapTarget,
    panelPosition,
    setFloatingWindows,
    floatWindowMinWidth,
    floatWindowMinHeight,
    setPanelGroups,
    setIsDraggingOutside,
    onMoveTab,
    onAddTabToMinimizedGroup,
    onPanelLongPressMouseDown,
    panelId,
    handleTabDrop,
    handleDropOnZone,
    setTabDropPosition,
    setFloatingWindowsExternal,
    disableTransitions,
    panelDragGhostHtml,
    setPanelDragGhostHtml,
    snapOverlay,
    onEmpty,
    iconAlignment = 'center',
}) => {
    const popupRef = useRef<HTMLDivElement | null>(null);
    const iconRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const iconGhostRef = useRef<HTMLElement | null>(null);
    /** Remembered popup geometry (localStorage-backed); a ref because it is
     * read inside mouse listeners whose closures must see the latest map. */
    const popupGeometryRef = useRef<Record<string, PopupGeometry> | null>(
        null,
    );
    if (popupGeometryRef.current === null) {
        popupGeometryRef.current = loadPopupGeometryMap();
    }
    /** Last computed frame of the active popup resize; consumed on mouseup
     * to persist the final geometry. */
    const lastResizeGeometryRef = useRef<{
        width: number;
        height: number;
        left: number;
        top: number;
    } | null>(null);
    /** True between a mousedown inside the popup and its click. A press that
     * starts inside and releases outside retargets the click to a common
     * ancestor outside popupRef — that click must never close the popup. */
    const popupPressGuardRef = useRef(false);
    const rememberPopupGeometry = (key: string, entry: PopupGeometry) => {
        popupGeometryRef.current = savePopupGeometryEntry(key, entry);
    };
    const readPopupGeometry = (key: string): PopupGeometry | undefined =>
        popupGeometryRef.current?.[key];
    const [pinned, setPinnedState] = useState(false);
    const t = usePanelLabel();
    const [iconOrder, setIconOrder] = useState<string[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [draggedIcon, setDraggedIcon] = useState<string | null>(null);
    const [dragOverIcon, setDragOverIcon] = useState<string | null>(null);
    const [iconShifts, setIconShifts] = useState<
        Record<string, 'left' | 'right' | 'up' | 'down' | null>
    >({});
    const [dragDirection, setDragDirection] = useState<
        'left' | 'right' | 'up' | 'down' | null
    >(null);
    const lastMousePos = useRef<{
        x: number;
        y: number;
        timestamp?: number;
        lastCalculatedPos?: { x: number; y: number };
    } | null>(null);
    const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const skipNextClickRef = useRef(false);
    const [isIconDragging, setIsIconDragging] = useState(false);
    const dragStartOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const draggedIndexRef = useRef<number>(-1);
    const targetIndexRef = useRef<number>(-1);
    const dragLongPressTimeout = useRef<NodeJS.Timeout | null>(null);
    const dragLongPressTriggered = useRef(false);
    const lastDragOverStateRef = useRef<{
        insertIndex: number;
        targetAxis: number;
        lastUpdateTime: number;
    }>({ insertIndex: -1, targetAxis: 0, lastUpdateTime: 0 });
    const THROTTLE_MS = 60;
    const allTabs = visibleGroups.flatMap((group) =>
        group.tabs.map((tab) => ({
            group,
            tab,
            refKey: `${group.id}-${tab.id}`,
            orderKey: `${group.id}::${tab.id}`,
        })),
    );
    const orderedTabs = iconOrder
        .map((orderKey) => allTabs.find((t) => t.orderKey === orderKey))
        .filter(Boolean) as typeof allTabs;
    const [cssVars, setCssVars] = useState({
        ICON_SIZE: 32,
        DRAG_LONG_PRESS_TIMEOUT: 300,
        DRAG_POSITION_THRESHOLD: 10,
    });
    useEffect(() => {
        function updateVars() {
            setCssVars({
                ICON_SIZE: getCssVarPx('--minimized-panel-icon-size', 32),
                DRAG_LONG_PRESS_TIMEOUT: getCssVarPx(
                    '--minimized-panel-drag-long-press-timeout',
                    300,
                ),
                DRAG_POSITION_THRESHOLD: getCssVarPx(
                    '--minimized-panel-drag-position-threshold',
                    10,
                ),
            });
        }
        updateVars();
        window.addEventListener('resize', updateVars);
        return () => window.removeEventListener('resize', updateVars);
    }, []);
    const ICON_SIZE = cssVars.ICON_SIZE;
    const DRAG_LONG_PRESS_TIMEOUT = cssVars.DRAG_LONG_PRESS_TIMEOUT;
    const DRAG_POSITION_THRESHOLD = cssVars.DRAG_POSITION_THRESHOLD;
    useEffect(() => {
        if (typeof window !== 'undefined' && !isInitialized) {
            try {
                const storedPinned = localStorage.getItem(PINNED_GLOBAL_KEY);
                if (storedPinned !== null) {
                    setPinnedState(storedPinned === 'true');
                }
                const storedOrder = localStorage.getItem(ICON_ORDER_KEY);
                if (storedOrder) {
                    setIconOrder(JSON.parse(storedOrder));
                }
                setIsInitialized(true);
            } catch {}
        }
    }, [isInitialized]);
    useEffect(() => {
        const ids = visibleGroups.flatMap((group) =>
            group.tabs.map((tab) => `${group.id}::${tab.id}`),
        );
        setIconOrder((prev) => {
            const seen = new Set<string>();
            const newOrder: string[] = [];
            prev.forEach((id) => {
                if (ids.includes(id) && !seen.has(id)) {
                    newOrder.push(id);
                    seen.add(id);
                }
            });
            ids.forEach((id) => {
                if (!seen.has(id)) {
                    newOrder.push(id);
                    seen.add(id);
                }
            });
            try {
                if (typeof window !== 'undefined' && isInitialized) {
                    localStorage.setItem(
                        ICON_ORDER_KEY,
                        JSON.stringify(newOrder),
                    );
                }
            } catch {}
            return newOrder;
        });
    }, [visibleGroups, isInitialized]);
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && isInitialized) {
                localStorage.setItem(ICON_ORDER_KEY, JSON.stringify(iconOrder));
            }
        } catch {}
    }, [iconOrder, isInitialized]);
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && isInitialized) {
                localStorage.setItem(
                    PINNED_GLOBAL_KEY,
                    pinned ? 'true' : 'false',
                );
            }
        } catch {}
    }, [pinned, isInitialized]);
    useEffect(() => {
        return () => {
            if (transitionTimeoutRef.current) {
                clearTimeout(transitionTimeoutRef.current);
            }
        };
    }, []);
    const setPinned = (fn: (prev: boolean) => boolean) => {
        setPinnedState((prev) => {
            const next = fn(prev);
            return next;
        });
    };
    const getPopupHeight = (tabId: string, groupId: string) => {
        const key = `${groupId}::${tabId}`;
        return popupHeights[key] ?? 180;
    };
    const setPopupHeightFor = (
        tabId: string,
        groupId: string,
        height: number,
    ) => {
        const key = `${groupId}::${tabId}`;
        setPopupHeights((prev) => ({ ...prev, [key]: height }));
    };
    const resizeStateRef = useRef<{
        startX: number;
        startY: number;
        startLeft: number;
        startTop: number;
        startWidth: number;
        startHeight: number;
        /** Icon position at resize start — the reference the popup's
         * icon-relative distances are measured against. */
        iconTop: number | null;
        iconCenterX: number | null;
    } | null>(null);

    const handlePopupResizeStart = (
        e: React.MouseEvent<HTMLDivElement>,
        handle: string,
    ) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const activeTab = popupTab;
        const currentWidth = popupWidth;
        const currentHeight = activeTab
            ? getPopupHeight(activeTab.tabId, activeTab.groupId)
            : 180;
        const iconRect = activeTab
            ? iconRefs.current[
                  `${activeTab.groupId}-${activeTab.tabId}`
              ]?.getBoundingClientRect()
            : undefined;
        resizeStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startLeft: popupPosition?.left ?? 0,
            startTop: popupPosition?.top ?? 0,
            startWidth: currentWidth,
            startHeight: currentHeight,
            iconTop: iconRect ? iconRect.top : null,
            iconCenterX: iconRect
                ? iconRect.left + iconRect.width / 2
                : null,
        };
        setPopupResizeHandle(handle);
    };

    useEffect(() => {
        if (!popupResizeHandle || !popupTab || !popupPosition) return;

        const handleMouseMove = (event: MouseEvent) => {
            const resize = resizeStateRef.current;
            if (!resize) return;

            const deltaX = event.clientX - resize.startX;
            const deltaY = event.clientY - resize.startY;
            const leftHandle = popupResizeHandle.includes('w');
            const rightHandle = popupResizeHandle.includes('e');
            const topHandle = popupResizeHandle.includes('n');
            const bottomHandle = popupResizeHandle.includes('s');

            let nextWidth = resize.startWidth;
            let nextHeight = resize.startHeight;
            let nextLeft = resize.startLeft;
            let nextTop = resize.startTop;

            // The dragged edge moves; the OPPOSITE edge stays fixed, and
            // since the rail-adjacent (anchored) edge never offers a handle,
            // that anchored edge is always the fixed one. Viewport bounds
            // cap the SIZE instead of shifting the fixed edge.
            if (leftHandle || rightHandle) {
                nextWidth = Math.max(
                    POPUP_MIN_WIDTH,
                    resize.startWidth + (rightHandle ? deltaX : -deltaX),
                );
                if (leftHandle) {
                    const fixedEastEdge =
                        resize.startLeft + resize.startWidth;
                    nextWidth = Math.min(
                        nextWidth,
                        Math.max(
                            POPUP_MIN_WIDTH,
                            fixedEastEdge - POPUP_VIEWPORT_MARGIN,
                        ),
                    );
                    nextLeft = fixedEastEdge - nextWidth;
                } else {
                    nextWidth = Math.min(
                        nextWidth,
                        Math.max(
                            POPUP_MIN_WIDTH,
                            window.innerWidth -
                                POPUP_VIEWPORT_MARGIN -
                                resize.startLeft,
                        ),
                    );
                    nextLeft = resize.startLeft;
                }
            }
            if (topHandle || bottomHandle) {
                nextHeight = Math.max(
                    POPUP_MIN_HEIGHT,
                    resize.startHeight + (bottomHandle ? deltaY : -deltaY),
                );
                if (topHandle) {
                    const fixedSouthEdge =
                        resize.startTop + resize.startHeight;
                    nextHeight = Math.min(
                        nextHeight,
                        Math.max(
                            POPUP_MIN_HEIGHT,
                            fixedSouthEdge - POPUP_VIEWPORT_MARGIN,
                        ),
                    );
                    nextTop = fixedSouthEdge - nextHeight;
                } else {
                    nextHeight = Math.min(
                        nextHeight,
                        Math.max(
                            POPUP_MIN_HEIGHT,
                            window.innerHeight -
                                POPUP_VIEWPORT_MARGIN -
                                resize.startTop,
                        ),
                    );
                    nextTop = resize.startTop;
                }
            }

            // Degenerate-space safety: the popup origin stays on screen.
            nextLeft = Math.max(
                POPUP_VIEWPORT_MARGIN,
                Math.min(nextLeft, window.innerWidth - POPUP_VIEWPORT_MARGIN),
            );
            nextTop = Math.max(
                POPUP_VIEWPORT_MARGIN,
                Math.min(nextTop, window.innerHeight - POPUP_VIEWPORT_MARGIN),
            );

            setPopupWidth(nextWidth);
            setPopupHeightFor(popupTab.tabId, popupTab.groupId, nextHeight);
            setPopupPosition({ left: nextLeft, top: nextTop });
            lastResizeGeometryRef.current = {
                width: nextWidth,
                height: nextHeight,
                left: nextLeft,
                top: nextTop,
            };
        };

        const handleMouseUp = () => {
            const last = lastResizeGeometryRef.current;
            const start = resizeStateRef.current;
            if (last && start) {
                const vertical =
                    panelPosition === 'left' || panelPosition === 'right';
                rememberPopupGeometry(
                    `${popupTab.groupId}::${popupTab.tabId}`,
                    {
                        width: Math.round(last.width),
                        height: Math.round(last.height),
                        // Distances to the icon at resize end, restored
                        // verbatim on the next open so the resized shape
                        // reappears relative to the icon as it was left.
                        offsetX:
                            !vertical && start.iconCenterX != null
                                ? Math.round(start.iconCenterX - last.left)
                                : 0,
                        offsetY:
                            vertical && start.iconTop != null
                                ? Math.round(start.iconTop - last.top)
                                : 0,
                        vertical,
                    },
                );
            }
            lastResizeGeometryRef.current = null;
            setPopupResizeHandle(null);
            resizeStateRef.current = null;
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [
        popupResizeHandle,
        popupTab,
        popupPosition,
        popupWidth,
        panelPosition,
        rememberPopupGeometry,
        setPopupHeightFor,
        setPopupPosition,
        setPopupWidth,
    ]);

    const handlePopupResizeStop = () => {
        setPopupResizeHandle(null);
        resizeStateRef.current = null;
    };
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (skipNextClickRef.current) {
                skipNextClickRef.current = false;
                return;
            }
            if (popupPressGuardRef.current) {
                // The press started inside the popup: a drag that releases
                // outside retargets the click to a common ancestor and must
                // not close the popup.
                popupPressGuardRef.current = false;
                return;
            }
            const target = e.target as HTMLElement;
            if (
                popupRef.current &&
                !popupRef.current.contains(target) &&
                !target.closest('[data-tab-icon="true"]') &&
                !target.closest('[role="tooltip"], .suite-ui-tooltip')
            ) {
                if (!pinned) {
                    setPopupTab(null);
                    setPopupPosition(null);
                }
            }
        }
        if (popupTab) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [popupTab, setPopupTab, setPopupPosition, pinned]);
    const setIconOrderWithLog = (fn: (prev: string[]) => string[]) => {
        setIconOrder((prev) => {
            const next = fn(prev);
            console.log('[setIconOrder]', { prev, next });
            return next;
        });
    };
    const setIconShiftsWithLog = (
        shifts: Record<string, 'left' | 'right' | 'up' | 'down' | null>,
    ) => {
        console.log('[setIconShifts]', { shifts });
        setIconShifts(shifts);
    };
    const setDraggedIconWithLog = (icon: string | null) => {
        console.log('[setDraggedIcon]', { icon });
        setDraggedIcon(icon);
    };
    const setIsIconDraggingWithLog = (val: boolean) => {
        console.log('[setIsIconDragging]', { val });
        setIsIconDragging(val);
    };
    const setDragDirectionWithLog = (
        dir: 'left' | 'right' | 'up' | 'down' | null,
    ) => {
        console.log('[setDragDirection]', { dir });
        setDragDirection(dir);
    };
    const calculateShifts = (currentPos: { x: number; y: number }) => {
        console.log('[calculateShifts] called', {
            currentPos,
            draggedIcon,
            lastMousePos: lastMousePos.current,
        });
        if (!lastMousePos.current || !draggedIcon) return;
        const isHorizontal =
            panelPosition === 'top' || panelPosition === 'bottom';
        const draggedIndex = draggedIndexRef.current;
        if (draggedIndex === -1) return;
        if (lastMousePos.current.lastCalculatedPos) {
            const delta = isHorizontal
                ? Math.abs(
                      currentPos.x - lastMousePos.current.lastCalculatedPos.x,
                  )
                : Math.abs(
                      currentPos.y - lastMousePos.current.lastCalculatedPos.y,
                  );
            if (delta < DRAG_POSITION_THRESHOLD) return;
        }
        let hoveredIndex = -1;
        let hoveredOrderKey: string | null = null;
        for (let i = 0; i < orderedTabs.length; i++) {
            const { refKey, orderKey } = orderedTabs[i];
            if (orderKey === draggedIcon) continue;
            const el = iconRefs.current[refKey];
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            const over = isHorizontal
                ? currentPos.x >= rect.left &&
                  currentPos.x <= rect.right &&
                  currentPos.y >= rect.top &&
                  currentPos.y <= rect.bottom
                : currentPos.y >= rect.top &&
                  currentPos.y <= rect.bottom &&
                  currentPos.x >= rect.left &&
                  currentPos.x <= rect.right;
            if (over) {
                hoveredIndex = i;
                hoveredOrderKey = orderKey;
                break;
            }
        }
        if (hoveredIndex === -1) {
            let closestDistance = Infinity;
            for (let i = 0; i < orderedTabs.length; i++) {
                if (i === draggedIndex) continue;
                const { refKey, orderKey } = orderedTabs[i];
                const el = iconRefs.current[refKey];
                if (!el) continue;
                const rect = el.getBoundingClientRect();
                const center = isHorizontal
                    ? rect.left + rect.width / 2
                    : rect.top + rect.height / 2;
                const mouse = isHorizontal ? currentPos.x : currentPos.y;
                const distance = Math.abs(center - mouse);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    hoveredIndex = i;
                    hoveredOrderKey = orderKey;
                }
            }
        }
        if (hoveredOrderKey !== dragOverIcon) {
            console.log('[calculateShifts] hoveredOrderKey changed', {
                hoveredOrderKey,
                dragOverIcon,
                hoveredIndex,
            });
            setDragOverIcon(hoveredOrderKey);
            if (hoveredIndex !== -1) {
                targetIndexRef.current = hoveredIndex;
                const newShifts: Record<
                    string,
                    'left' | 'right' | 'up' | 'down' | null
                > = {};
                orderedTabs.forEach((tab) => {
                    newShifts[tab.orderKey] = null;
                });
                if (draggedIndex !== -1) {
                    const direction = isHorizontal
                        ? hoveredIndex > draggedIndex
                            ? 'right'
                            : 'left'
                        : hoveredIndex > draggedIndex
                          ? 'down'
                          : 'up';
                    for (let i = 0; i < orderedTabs.length; i++) {
                        if (
                            direction === 'right' &&
                            i > draggedIndex &&
                            i <= hoveredIndex
                        )
                            newShifts[orderedTabs[i].orderKey] = 'left';
                        else if (
                            direction === 'left' &&
                            i < draggedIndex &&
                            i >= hoveredIndex
                        )
                            newShifts[orderedTabs[i].orderKey] = 'right';
                        else if (
                            direction === 'down' &&
                            i > draggedIndex &&
                            i <= hoveredIndex
                        )
                            newShifts[orderedTabs[i].orderKey] = 'up';
                        else if (
                            direction === 'up' &&
                            i < draggedIndex &&
                            i >= hoveredIndex
                        )
                            newShifts[orderedTabs[i].orderKey] = 'down';
                    }
                }
                setIconShiftsWithLog(newShifts);
                queueMicrotask(() => {
                    orderedTabs.forEach(({ refKey, orderKey }) => {
                        const el = iconRefs.current[refKey];
                        if (!el) return;
                        el.classList.remove(
                            'shift-left',
                            'shift-right',
                            'shift-up',
                            'shift-down',
                            'floating-drag-shift',
                        );
                        const shiftClass = newShifts[orderKey];
                        if (shiftClass) {
                            el.classList.add(
                                'floating-drag-shift',
                                `shift-${shiftClass}`,
                            );
                        }
                    });
                });
            }
        }
        lastMousePos.current = {
            ...lastMousePos.current,
            lastCalculatedPos: { x: currentPos.x, y: currentPos.y },
        };
    };
    const globalDrag = globalDragState.getState();
    const isFloatingPanelDragging =
        globalDrag &&
        globalDrag.isDragging &&
        globalDrag.sourceType === 'floating';
    const handleDragOver = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            console.log('[handleDragOver] event', {
                isIconDragging,
                draggedIcon,
                types: e.dataTransfer.types,
            });
            e.preventDefault();
            const tabsIconsEl = e.currentTarget as HTMLElement;
            if (!tabsIconsEl) return;
            if (
                e.dataTransfer.types.includes('application/floating-window') ||
                isFloatingPanelDragging
            ) {
                e.dataTransfer.dropEffect = 'move';
                const isHorizontal =
                    panelPosition === 'top' || panelPosition === 'bottom';
                const currentPos = isHorizontal ? e.clientX : e.clientY;
                const now = Date.now();
                if (
                    now - lastDragOverStateRef.current.lastUpdateTime <
                    THROTTLE_MS
                )
                    return;
                const iconCenters: { index: number; pos: number }[] = [];
                for (let i = 0; i < orderedTabs.length; i++) {
                    const icon = iconRefs.current[orderedTabs[i].refKey];
                    if (!icon) continue;
                    const rect = icon.getBoundingClientRect();
                    const centerPos = isHorizontal
                        ? rect.left + rect.width / 2
                        : rect.top + rect.height / 2;
                    iconCenters.push({ index: i, pos: centerPos });
                }
                iconCenters.sort((a, b) => a.pos - b.pos);
                let insertBeforeIndex = orderedTabs.length;
                for (let i = 0; i < iconCenters.length; i++) {
                    if (currentPos < iconCenters[i].pos) {
                        insertBeforeIndex = iconCenters[i].index;
                        break;
                    }
                }
                if (
                    insertBeforeIndex ===
                        lastDragOverStateRef.current.insertIndex &&
                    Math.abs(
                        currentPos - lastDragOverStateRef.current.targetAxis,
                    ) < 20
                )
                    return;
                lastDragOverStateRef.current = {
                    insertIndex: insertBeforeIndex,
                    targetAxis: currentPos,
                    lastUpdateTime: now,
                };
                tabsIconsEl.dataset.insertIndex = insertBeforeIndex.toString();
                tabsIconsEl.dataset.panelPosition = panelPosition || 'left';
                const newShifts: Record<
                    string,
                    'left' | 'right' | 'up' | 'down' | null
                > = {};
                orderedTabs.forEach((item) => {
                    newShifts[item.orderKey] = null;
                });
                const tabIcons = Array.from(
                    (e.currentTarget as HTMLDivElement).querySelectorAll(
                        '.tab-icon-wrapper',
                    ),
                );
                tabIcons.forEach((icon, index) => {
                    icon.classList.remove(
                        'floating-drag-shift',
                        'shift-left',
                        'shift-right',
                        'shift-up',
                        'shift-down',
                    );
                });
                const globalDrag =
                    globalDragState.getState && globalDragState.getState();
                const isCrossPanel =
                    globalDrag &&
                    globalDrag.isDragging &&
                    globalDrag.sourcePanelId !== panelId;
                if (isCrossPanel) {
                    tabIcons.forEach((icon, index) => {
                        if (index >= insertBeforeIndex) {
                            icon.classList.add('floating-drag-shift');
                            if (isHorizontal) icon.classList.add('shift-right');
                            else icon.classList.add('shift-down');
                        }
                    });
                } else {
                    tabIcons.forEach((icon, index) => {
                        if (
                            draggedIndexRef.current < insertBeforeIndex &&
                            index > draggedIndexRef.current &&
                            index < insertBeforeIndex
                        ) {
                            icon.classList.add('floating-drag-shift');
                            if (isHorizontal) icon.classList.add('shift-left');
                            else icon.classList.add('shift-up');
                        } else if (
                            draggedIndexRef.current > insertBeforeIndex &&
                            index >= insertBeforeIndex &&
                            index < draggedIndexRef.current
                        ) {
                            icon.classList.add('floating-drag-shift');
                            if (isHorizontal) icon.classList.add('shift-right');
                            else icon.classList.add('shift-down');
                        }
                    });
                }
                tabsIconsEl.dataset.insertIndex = insertBeforeIndex.toString();
                tabsIconsEl.dataset.panelPosition = panelPosition || 'left';
                if (draggedIndexRef.current === -1) {
                    for (
                        let i = insertBeforeIndex;
                        i < orderedTabs.length;
                        i++
                    ) {
                        if (isHorizontal)
                            newShifts[orderedTabs[i].orderKey] = 'right';
                        else newShifts[orderedTabs[i].orderKey] = 'down';
                    }
                } else {
                    orderedTabs.forEach((item, index) => {
                        if (index >= insertBeforeIndex) {
                            if (isHorizontal)
                                newShifts[item.orderKey] = 'right';
                            else newShifts[item.orderKey] = 'down';
                        }
                    });
                }
                let shiftsChanged = false;
                for (const key in newShifts) {
                    if (newShifts[key] !== iconShifts[key]) {
                        shiftsChanged = true;
                        break;
                    }
                }
                if (shiftsChanged) {
                    setIconShiftsWithLog(newShifts);
                    queueMicrotask(() => {
                        orderedTabs.forEach(({ refKey, orderKey }) => {
                            const el = iconRefs.current[refKey];
                            if (!el) return;
                            el.classList.remove(
                                'shift-left',
                                'shift-right',
                                'shift-up',
                                'shift-down',
                                'floating-drag-shift',
                            );
                            const shiftClass = newShifts[orderKey];
                            if (shiftClass) {
                                el.classList.add(
                                    'floating-drag-shift',
                                    `shift-${shiftClass}`,
                                );
                            }
                        });
                    });
                }
                return;
            }
            if (!isIconDragging || !draggedIcon) return;
            const mousePos = { x: e.clientX, y: e.clientY };
            const now = Date.now();
            if (now - (lastMousePos.current?.timestamp || 0) < THROTTLE_MS)
                return;
            lastMousePos.current = {
                x: mousePos.x,
                y: mousePos.y,
                timestamp: now,
            };
            calculateShifts(mousePos);
        },
        [
            isIconDragging,
            draggedIcon,
            orderedTabs,
            panelPosition,
            iconShifts,
            calculateShifts,
            THROTTLE_MS,
        ],
    );
    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const isInside =
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom;
        if (isInside || e.currentTarget.contains(e.relatedTarget as Node)) {
            return;
        }
        if (e.dataTransfer.types.includes('application/floating-window')) {
            setIconShifts({});
            delete (e.currentTarget as HTMLDivElement).dataset.insertIndex;
            clearFloatingDragClasses();
            lastDragOverStateRef.current = {
                insertIndex: -1,
                targetAxis: 0,
                lastUpdateTime: 0,
            };
        }
    };
    useEffect(() => {
        function handleFloatingDragEnd(e: DragEvent) {
            setIconShifts({});
            lastDragOverStateRef.current = {
                insertIndex: -1,
                targetAxis: 0,
                lastUpdateTime: 0,
            };
            document.querySelectorAll('.tabs-icons').forEach((el) => {
                if ('dataset' in el) {
                    delete (el as HTMLElement).dataset.insertIndex;
                }
            });
            clearFloatingDragClasses();
        }
        window.addEventListener('dragend', handleFloatingDragEnd);
        return () =>
            window.removeEventListener('dragend', handleFloatingDragEnd);
    }, []);
    const clearFloatingDragClasses = () => {
        document
            .querySelectorAll(
                '.floating-drag-shift, .shift-left, .shift-right, .shift-up, .shift-down',
            )
            .forEach((el) => {
                el.classList.remove(
                    'floating-drag-shift',
                    'shift-left',
                    'shift-right',
                    'shift-up',
                    'shift-down',
                );
            });
        document.querySelectorAll('.tabs-icons').forEach((el) => {
            if ('dataset' in el) {
                delete (el as HTMLElement).dataset.insertIndex;
                delete (el as HTMLElement).dataset.panelPosition;
            }
        });
        lastDragOverStateRef.current = {
            insertIndex: -1,
            targetAxis: 0,
            lastUpdateTime: 0,
        };
    };
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        console.log('[handleDrop] event', {
            draggedIcon,
            targetIndex: targetIndexRef.current,
            draggedIndex: draggedIndexRef.current,
        });
        e.preventDefault();
        const insertIndex = e.currentTarget.dataset.insertIndex
            ? parseInt(e.currentTarget.dataset.insertIndex, 10)
            : orderedTabs.length;
        const panelPos = e.currentTarget.dataset.panelPosition || panelPosition;
        console.log(
            'MinimizedSidePanel drop - Insert index:',
            insertIndex,
            'Panel position:',
            panelPos,
        );
        setIconShifts({});
        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
        }
        try {
            const floatingWindowData = e.dataTransfer.getData(
                'application/floating-window',
            );
            if (floatingWindowData) {
                const { windowId, tabId } = JSON.parse(floatingWindowData);
                if (windowId && tabId) {
                    let tabInfo = allTabs.find(
                        (t) => t.tab.id === tabId && t.group.id === windowId,
                    );
                    const isHorizontal =
                        panelPos === 'top' || panelPos === 'bottom';
                    console.log(
                        `FloatingWindowDrop - Panel position: ${panelPos}, isHorizontal: ${isHorizontal}`,
                    );
                    if (!tabInfo && setFloatingWindows) {
                        setFloatingWindows((fws) => {
                            const fw = fws.find(
                                (fw) =>
                                    fw.tab.id === tabId && fw.id === windowId,
                            );
                            if (fw) {
                                queueMicrotask(() => {
                                    setPanelGroups?.((prev) => {
                                        let found = false;
                                        const newGroups = prev.map((group) => {
                                            if (group.id === windowId) {
                                                found = true;
                                                if (
                                                    group.tabs.some(
                                                        (t) => t.id === tabId,
                                                    )
                                                )
                                                    return group;
                                                return {
                                                    ...group,
                                                    isMinimized: true,
                                                    tabs: [
                                                        ...group.tabs,
                                                        fw.tab,
                                                    ],
                                                };
                                            }
                                            return group;
                                        });
                                        if (!found) {
                                            newGroups.push({
                                                id: windowId,
                                                isVisible: true,
                                                isMinimized: true,
                                                height: 40,
                                                tabs: [fw.tab],
                                            });
                                        }
                                        return newGroups;
                                    });
                                    setIconOrder((prev) => {
                                        let newOrder = prev.filter(
                                            (key) =>
                                                key !== `${windowId}::${tabId}`,
                                        );
                                        if (
                                            !newOrder.includes(
                                                `${windowId}::${tabId}`,
                                            )
                                        ) {
                                            if (
                                                typeof insertIndex ===
                                                    'number' &&
                                                insertIndex >= 0 &&
                                                insertIndex <= newOrder.length
                                            ) {
                                                newOrder.splice(
                                                    insertIndex,
                                                    0,
                                                    `${windowId}::${tabId}`,
                                                );
                                            } else {
                                                newOrder.push(
                                                    `${windowId}::${tabId}`,
                                                );
                                            }
                                        }
                                        return newOrder;
                                    });
                                    setIconShifts({});
                                    clearFloatingDragClasses();
                                    if (transitionTimeoutRef.current) {
                                        clearTimeout(
                                            transitionTimeoutRef.current,
                                        );
                                    }
                                });
                                return fws.filter(
                                    (fw) =>
                                        !(
                                            fw.tab.id === tabId &&
                                            fw.id === windowId
                                        ),
                                );
                            }
                            return fws;
                        });
                        return;
                    }
                    if (onFloatingWindowDrop) {
                        onFloatingWindowDrop({ windowId, tabId, insertIndex });
                        delete e.currentTarget.dataset.insertIndex;
                        delete e.currentTarget.dataset.panelPosition;
                        setDragOverIcon(null);
                        setDragDirection(null);
                        return;
                    }
                }
            }
        } catch (error) {
            console.warn('Error parsing floating window data:', error);
        }
        document
            .querySelectorAll(
                '.tab-icon-wrapper.floating-drag-shift, .tab-icon-wrapper.shift-left, .tab-icon-wrapper.shift-right, .tab-icon-wrapper.shift-up, .tab-icon-wrapper.shift-down',
            )
            .forEach((el) => {
                el.classList.remove(
                    'floating-drag-shift',
                    'shift-left',
                    'shift-right',
                    'shift-up',
                    'shift-down',
                );
            });
        if (
            draggedIcon &&
            targetIndexRef.current !== -1 &&
            draggedIndexRef.current !== -1
        ) {
            const sourceIndex = draggedIndexRef.current;
            const targetIndex = targetIndexRef.current;
            if (sourceIndex !== targetIndex) {
                setIconOrderWithLog((prev) => {
                    const newOrder = [...prev];
                    const [removed] = newOrder.splice(sourceIndex, 1);
                    if (!newOrder.includes(removed)) {
                        newOrder.splice(
                            targetIndex > sourceIndex
                                ? targetIndex - 1
                                : targetIndex,
                            0,
                            removed,
                        );
                    }
                    return newOrder;
                });
            }
        }
        setDragOverIcon(null);
        setDragDirection(null);
        draggedIndexRef.current = -1;
        targetIndexRef.current = -1;
        skipNextClickRef.current = true;
    };
    const handleIconMouseDown = (
        e: React.MouseEvent,
        orderKey: string,
        icon: React.ReactNode,
    ) => {
        console.log('[handleIconMouseDown] event', { orderKey });
        e.preventDefault();
        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
            transitionTimeoutRef.current = null;
        }
        dragLongPressTriggered.current = false;
        const draggedIndex = orderedTabs.findIndex(
            (tab) => tab.orderKey === orderKey,
        );
        if (draggedIndex === -1) return;
        draggedIndexRef.current = draggedIndex;
        targetIndexRef.current = draggedIndex;
        dragLongPressTimeout.current = setTimeout(() => {
            dragLongPressTriggered.current = true;
            setDraggedIconWithLog(orderKey);
            setIsIconDraggingWithLog(true);
            if (iconGhostRef.current) {
                iconGhostRef.current.remove();
                iconGhostRef.current = null;
            }
            const iconEl = iconRefs.current[orderedTabs[draggedIndex].refKey];
            if (iconEl) {
                const rect = iconEl.getBoundingClientRect();
                const ghost = iconEl.cloneNode(true) as HTMLElement;
                ghost.classList.add('minimized-panel-ghost');
                ghost.style.position = 'fixed';
                ghost.style.left = `${rect.left}px`;
                ghost.style.top = `${rect.top}px`;
                ghost.style.width = `${rect.width}px`;
                ghost.style.height = `${rect.height}px`;
                ghost.style.opacity = '0.5';
                ghost.style.pointerEvents = 'none';
                ghost.style.zIndex = '10000';
                document.body.appendChild(ghost);
                iconGhostRef.current = ghost;
            }
            const handleGhostMove = (ev: MouseEvent) => {
                if (iconGhostRef.current) {
                    iconGhostRef.current.style.left = `${ev.clientX - iconGhostRef.current.offsetWidth / 2}px`;
                    iconGhostRef.current.style.top = `${ev.clientY - iconGhostRef.current.offsetHeight / 2}px`;
                }
            };
            const handleGhostUp = () => {
                if (iconGhostRef.current) {
                    iconGhostRef.current.remove();
                    iconGhostRef.current = null;
                }
                window.removeEventListener('mousemove', handleGhostMove);
                window.removeEventListener('mouseup', handleGhostUp);
            };
            window.addEventListener('mousemove', handleGhostMove);
            window.addEventListener('mouseup', handleGhostUp);
            document.body.classList.add('dragging-sidepanel-icon');
            const tabInfo = orderedTabs[draggedIndex];
            globalDragState.setState({
                isDragging: true,
                sourceType: 'minimized',
                sourcePanelId: panelId,
                sourceGroupId: tabInfo.group.id,
                sourceTabId: tabInfo.tab.id,
                position: { x: e.clientX, y: e.clientY },
                tabData: tabInfo.tab,
                draggedIcon: orderKey,
                isIconDragging: true,
                ghostType: 'minimized',
                ghostData: {
                    panelId,
                    groupId: tabInfo.group.id,
                    tabId: tabInfo.tab.id,
                    icon,
                    orderKey,
                },
                dragGhostIcon: icon,
                dragGhostPos: { x: e.clientX, y: e.clientY },
            });
        }, DRAG_LONG_PRESS_TIMEOUT);
        const cancelLongPress = () => {
            if (dragLongPressTimeout.current) {
                clearTimeout(dragLongPressTimeout.current);
                dragLongPressTimeout.current = null;
            }
        };
        const handleMouseUp = (ev: MouseEvent) => {
            // Detach FIRST, unconditionally: the toggle-close path below
            // returns before the trailing removal, which used to leak this
            // listener until it fired on an unrelated mouseup (e.g. pressing
            // the popup pin) and toggled the popup with a stale closure.
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('mouseleave', handleMouseUp);
            cancelLongPress();
            if (!dragLongPressTriggered.current) {
                if (
                    popupTab &&
                    popupTab.tabId === orderedTabs[draggedIndex].tab.id &&
                    popupTab.groupId === orderedTabs[draggedIndex].group.id
                ) {
                    setPopupTab(null);
                    setPopupPosition(null);
                    return;
                }
                const tabInfo = orderedTabs[draggedIndex];
                const storedGeometry = readPopupGeometry(
                    `${tabInfo.group.id}::${tabInfo.tab.id}`,
                );
                // Icon distances only apply when the geometry was recorded on
                // the same rail axis; the remembered size applies regardless.
                const isVerticalRail = !(
                    panelPosition === 'top' || panelPosition === 'bottom'
                );
                const geometry =
                    storedGeometry &&
                    storedGeometry.vertical === isVerticalRail
                        ? storedGeometry
                        : undefined;
                const openWidth = storedGeometry?.width ?? popupWidth;
                const openHeight =
                    storedGeometry?.height ??
                    getPopupHeight(tabInfo.tab.id, tabInfo.group.id);
                if (storedGeometry) {
                    setPopupWidth(openWidth);
                    setPopupHeightFor(
                        tabInfo.tab.id,
                        tabInfo.group.id,
                        openHeight,
                    );
                }
                const iconEl =
                    iconRefs.current[orderedTabs[draggedIndex].refKey];
                const panelEl = panelRef.current;
                if (iconEl && panelEl) {
                    const rect = iconEl.getBoundingClientRect();
                    const panelRect = panelEl.getBoundingClientRect();
                    // Vertical rails anchor the popup beside the rail and
                    // hang its top edge at the remembered distance from the
                    // icon; horizontal rails anchor to the rail edge and
                    // keep the remembered icon-center distance.
                    let left = panelRect.left;
                    let top = rect.top - (geometry?.offsetY ?? 0);
                    if (panelPosition === 'left') {
                        left = panelRect.right;
                    } else if (panelPosition === 'right') {
                        left = panelRect.left - openWidth;
                    } else if (panelPosition === 'top') {
                        left = rect.left;
                        top = panelRect.bottom + 5;
                    } else if (panelPosition === 'bottom') {
                        left = rect.left;
                        top = panelRect.top - openHeight - 5;
                    }
                    if (top + openHeight > window.innerHeight) {
                        top = window.innerHeight - openHeight - 8;
                        if (top < 0) top = 8;
                    }
                    if (left + openWidth > window.innerWidth) {
                        left = window.innerWidth - openWidth - 8;
                        if (left < 0) left = 8;
                    }
                    if (panelPosition === 'top' || panelPosition === 'bottom') {
                        left =
                            rect.left +
                            rect.width / 2 -
                            (geometry?.offsetX || openWidth / 2);
                        if (left < 8) left = 8;
                        if (left + openWidth > window.innerWidth - 8) {
                            left = window.innerWidth - openWidth - 8;
                        }
                    }
                    setPopupPosition({ left, top });
                }
                setPopupTab({
                    tabId: tabInfo.tab.id,
                    groupId: tabInfo.group.id,
                });
            }
            const prev = globalDragState.getState();
            globalDragState.setState({
                ...prev,
                isDragging: false,
                position: { x: ev.clientX, y: ev.clientY },
                sourceType: prev?.sourceType || 'minimized',
            });
            globalDragState.clear();
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('mouseleave', handleMouseUp);
        };
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('mouseleave', handleMouseUp);
    };
    const handlePanelLongPressMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (
            target.closest('.tab-icon-wrapper') ||
            target.closest('.react-resizable-handle') ||
            target.closest('.sidepanel-popup') ||
            target.closest('.sidepanel-popup-body') ||
            target.closest('.sidepanel-popup-header') ||
            target.closest('.panel-tab-content') ||
            target.closest('.side-panel-header-btn') ||
            target.closest('.side-panel-header-actions')
        ) {
            return;
        }
        if (isDraggingPanel || isIconDragging) return;
        dragLongPressTriggered.current = false;
        if (dragLongPressTimeout.current)
            clearTimeout(dragLongPressTimeout.current);
        dragLongPressTimeout.current = setTimeout(() => {
            if (isDraggingPanel || isIconDragging) return;
            dragLongPressTriggered.current = true;
            if (panelRef.current && setPanelDragGhostHtml) {
                const sidePanelElement =
                    panelRef.current.closest('.side-panel');
                if (sidePanelElement) {
                    setPanelDragGhostHtml(sidePanelElement.outerHTML);
                    onPanelLongPressMouseDown?.(e);
                } else {
                    setPanelDragGhostHtml(null);
                }
            }
        }, DRAG_LONG_PRESS_TIMEOUT);
        const cancelLongPress = () => {
            if (dragLongPressTimeout.current) {
                clearTimeout(dragLongPressTimeout.current);
                dragLongPressTimeout.current = null;
            }
        };
        const handleUp = () => {
            cancelLongPress();
            window.removeEventListener('mouseup', handleUp);
            window.removeEventListener('mouseleave', handleUp);
        };
        window.addEventListener('mouseup', handleUp);
        window.addEventListener('mouseleave', handleUp);
    };
    useEffect(() => {
        if (!isDraggingPanel && setPanelDragGhostHtml && panelDragGhostHtml) {
            setPanelDragGhostHtml(null);
        }
    }, [isDraggingPanel, setPanelDragGhostHtml, panelDragGhostHtml]);
    const lastTabTargetRef = useRef<HTMLElement | null>(null);
    const lastDropZoneRef = useRef<HTMLElement | null>(null);
    const lastDropPositionRef = useRef<'left' | 'right' | null>(null);
    useEffect(() => {
        if (!isIconDragging || !draggedIcon) return;
        const handleGlobalMouseMove = (e: MouseEvent) => {
            const elements = document.elementsFromPoint(e.clientX, e.clientY);
            const tabsIconsEl = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('tabs-icons'),
            ) as HTMLElement | null;
            const sidePanelElement = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('side-panel'),
            ) as HTMLElement | null;
            const minimizedPanelElement = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('minimized-sidebar-container'),
            ) as HTMLElement | null;
            const targetPanelId =
                sidePanelElement?.getAttribute('data-panel-id') || '';
            const tabTarget = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('panel-tab') &&
                    !el.classList.contains('dragging'),
            ) as HTMLElement | null;
            const dropZoneTarget = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('group-drop-zone'),
            ) as HTMLElement | null;
            document
                .querySelectorAll(
                    '.panel-tab.drop-target-left, .panel-tab.drop-target-right',
                )
                .forEach((el) => {
                    el.classList.remove(
                        'drop-target-left',
                        'drop-target-right',
                    );
                });
            document
                .querySelectorAll('.group-drop-zone.active')
                .forEach((zone) => {
                    zone.classList.remove('active');
                });
            if (tabTarget) {
                const tabRect = tabTarget.getBoundingClientRect();
                const isLeft = e.clientX < tabRect.left + tabRect.width / 2;
                tabTarget.classList.add(
                    isLeft ? 'drop-target-left' : 'drop-target-right',
                );
            }
            if (dropZoneTarget) {
                dropZoneTarget.classList.add('active');
            }
            if (tabsIconsEl && targetPanelId && targetPanelId !== panelId) {
                const isHorizontal =
                    tabsIconsEl.classList.contains('horizontal');
                const tabIcons = Array.from(
                    tabsIconsEl.querySelectorAll('.tab-icon-wrapper'),
                );
                const iconCenters = tabIcons.map((icon, index) => {
                    const rect = icon.getBoundingClientRect();
                    return {
                        index,
                        pos: isHorizontal
                            ? rect.left + rect.width / 2
                            : rect.top + rect.height / 2,
                    };
                });
                iconCenters.sort((a, b) => a.pos - b.pos);
                let insertBeforeIndex = tabIcons.length;
                const currentPos = isHorizontal ? e.clientX : e.clientY;
                for (let i = 0; i < iconCenters.length; i++) {
                    if (currentPos < iconCenters[i].pos) {
                        insertBeforeIndex = iconCenters[i].index;
                        break;
                    }
                }
                tabIcons.forEach((icon, index) => {
                    icon.classList.remove(
                        'floating-drag-shift',
                        'shift-left',
                        'shift-right',
                        'shift-up',
                        'shift-down',
                    );
                    if (index >= insertBeforeIndex) {
                        icon.classList.add('floating-drag-shift');
                        if (isHorizontal) icon.classList.add('shift-right');
                        else icon.classList.add('shift-down');
                    }
                });
                tabsIconsEl.dataset.insertIndex = insertBeforeIndex.toString();
                return;
            }
            if (tabsIconsEl && targetPanelId === panelId) {
                const isHorizontal =
                    tabsIconsEl.classList.contains('horizontal');
                const tabIcons = Array.from(
                    tabsIconsEl.querySelectorAll('.tab-icon-wrapper'),
                );
                const iconCenters = tabIcons.map((icon, index) => {
                    const rect = icon.getBoundingClientRect();
                    return {
                        index,
                        pos: isHorizontal
                            ? rect.left + rect.width / 2
                            : rect.top + rect.height / 2,
                    };
                });
                iconCenters.sort((a, b) => a.pos - b.pos);
                let insertBeforeIndex = tabIcons.length;
                const currentPos = isHorizontal ? e.clientX : e.clientY;
                for (let i = 0; i < iconCenters.length; i++) {
                    if (currentPos < iconCenters[i].pos) {
                        insertBeforeIndex = iconCenters[i].index;
                        break;
                    }
                }
                tabIcons.forEach((icon, index) => {
                    icon.classList.remove(
                        'floating-drag-shift',
                        'shift-left',
                        'shift-right',
                        'shift-up',
                        'shift-down',
                    );
                    if (
                        draggedIndexRef.current < insertBeforeIndex &&
                        index > draggedIndexRef.current &&
                        index < insertBeforeIndex
                    ) {
                        icon.classList.add('floating-drag-shift');
                        if (isHorizontal) icon.classList.add('shift-left');
                        else icon.classList.add('shift-up');
                    } else if (
                        draggedIndexRef.current > insertBeforeIndex &&
                        index >= insertBeforeIndex &&
                        index < draggedIndexRef.current
                    ) {
                        icon.classList.add('floating-drag-shift');
                        if (isHorizontal) icon.classList.add('shift-right');
                        else icon.classList.add('shift-down');
                    }
                });
                tabsIconsEl.dataset.insertIndex = insertBeforeIndex.toString();
                return;
            }
            document
                .querySelectorAll(
                    '.tab-icon-wrapper.floating-drag-shift, .tab-icon-wrapper.shift-left, .tab-icon-wrapper.shift-right, .tab-icon-wrapper.shift-up, .tab-icon-wrapper.shift-down',
                )
                .forEach((el) => {
                    el.classList.remove(
                        'floating-drag-shift',
                        'shift-left',
                        'shift-right',
                        'shift-up',
                        'shift-down',
                    );
                });
            document.querySelectorAll('.tabs-icons').forEach((el) => {
                if ('dataset' in el) {
                    delete (el as HTMLElement).dataset.insertIndex;
                }
            });
        };
        const handleGlobalMouseUp = (e: MouseEvent) => {
            const elements = document.elementsFromPoint(e.clientX, e.clientY);
            const sidePanelElement = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('side-panel'),
            ) as HTMLElement | null;
            const minimizedPanelElement = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('minimized-sidebar-container'),
            ) as HTMLElement | null;
            const targetPanelId =
                sidePanelElement?.getAttribute('data-panel-id') || '';
            const tabsIconsEl = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('tabs-icons'),
            ) as HTMLElement | null;
            const tabTarget = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('panel-tab') &&
                    !el.classList.contains('dragging'),
            ) as HTMLElement | null;
            const dropZoneTarget = elements.find(
                (el) =>
                    el instanceof HTMLElement &&
                    el.classList.contains('group-drop-zone'),
            ) as HTMLElement | null;
            if (
                minimizedPanelElement &&
                draggedIcon &&
                targetPanelId &&
                targetPanelId !== panelId
            ) {
                const tabInfo = orderedTabs.find(
                    (t) => t.orderKey === draggedIcon,
                );
                if (tabInfo) {
                    let insertIndex: number | undefined = undefined;
                    if (tabsIconsEl && tabsIconsEl.dataset.insertIndex) {
                        insertIndex = parseInt(
                            tabsIconsEl.dataset.insertIndex,
                            10,
                        );
                    }
                    console.log(
                        '[handleGlobalMouseUp][minimized] Dispatch transfer-tab-between-panels',
                        {
                            tab: tabInfo.tab,
                            sourcePanel: String(panelId),
                            sourceGroup: tabInfo.group,
                            sourceTabOrderKey: tabInfo.orderKey,
                            targetPanel: String(targetPanelId),
                            insertIndex,
                            iconOrder,
                            orderedTabs: orderedTabs.map((t) => t.orderKey),
                        },
                    );
                    const minimizedDropEvent = new CustomEvent(
                        PANEL_EVENTS.transferTab,
                        {
                            detail: {
                                tab: tabInfo.tab,
                                sourcePanel: String(panelId),
                                sourceGroup: tabInfo.group,
                                sourceTabOrderKey: tabInfo.orderKey,
                                targetPanel: String(targetPanelId),
                                insertIndex,
                            },
                        },
                    );
                    window.dispatchEvent(minimizedDropEvent);
                }
            } else if (
                (tabTarget || dropZoneTarget || sidePanelElement) &&
                draggedIcon &&
                targetPanelId &&
                targetPanelId !== panelId
            ) {
                if (tabTarget) {
                    const targetTabId =
                        tabTarget.getAttribute('data-tab-id') || '';
                    const targetGroupElement =
                        tabTarget.closest('[data-group-id]');
                    const targetGroupId =
                        targetGroupElement instanceof HTMLElement
                            ? targetGroupElement.getAttribute(
                                  'data-group-id',
                              ) || ''
                            : '';
                    const tabRect = tabTarget.getBoundingClientRect();
                    const pos =
                        e.clientX < tabRect.left + tabRect.width / 2
                            ? 'left'
                            : 'right';
                    const tabInfo = orderedTabs.find(
                        (t) => t.orderKey === draggedIcon,
                    );
                    if (tabInfo) {
                        const transferEvent = new CustomEvent(
                            PANEL_EVENTS.transferTab,
                            {
                                detail: {
                                    tab: tabInfo.tab,
                                    sourcePanel: String(tabInfo.group.id),
                                    sourceGroup: tabInfo.group,
                                    sourceTabOrderKey: tabInfo.orderKey,
                                    targetPanel: String(targetPanelId),
                                    targetGroupId,
                                    targetTabId,
                                    position: pos,
                                    insertIndex: undefined,
                                },
                            },
                        );
                        window.dispatchEvent(transferEvent);
                    }
                } else if (dropZoneTarget) {
                    const beforeGroupId =
                        dropZoneTarget.getAttribute('data-before-group');
                    const afterGroupId =
                        dropZoneTarget.getAttribute('data-after-group');
                    const zoneId = beforeGroupId
                        ? beforeGroupId
                        : afterGroupId
                          ? afterGroupId
                          : undefined;
                    const position = beforeGroupId ? 'before' : 'after';
                    const tabInfo = orderedTabs.find(
                        (t) => t.orderKey === draggedIcon,
                    );
                    if (tabInfo && zoneId) {
                        const transferEvent = new CustomEvent(
                            PANEL_EVENTS.transferTab,
                            {
                                detail: {
                                    tab: tabInfo.tab,
                                    sourcePanel: tabInfo.group.id,
                                    sourceGroup: tabInfo.group,
                                    sourceTabOrderKey: tabInfo.orderKey,
                                    targetPanel: targetPanelId,
                                    targetGroupId: zoneId,
                                    targetTabId: undefined,
                                    position,
                                    insertIndex: undefined,
                                },
                            },
                        );
                        window.dispatchEvent(transferEvent);
                    }
                }
            } else if (tabsIconsEl && draggedIcon) {
                const isHorizontal =
                    tabsIconsEl.classList.contains('horizontal');
                const tabIcons = Array.from(
                    tabsIconsEl.querySelectorAll('.tab-icon-wrapper'),
                );
                const iconCenters: { index: number; pos: number }[] = [];
                tabIcons.forEach((icon, index) => {
                    const iconRect = icon.getBoundingClientRect();
                    const centerPos = isHorizontal
                        ? iconRect.left + iconRect.width / 2
                        : iconRect.top + iconRect.height / 2;
                    iconCenters.push({ index, pos: centerPos });
                });
                iconCenters.sort((a, b) => a.pos - b.pos);
                let insertBeforeIndex = tabIcons.length;
                const currentPos = isHorizontal ? e.clientX : e.clientY;
                for (let i = 0; i < iconCenters.length; i++) {
                    if (currentPos < iconCenters[i].pos) {
                        insertBeforeIndex = iconCenters[i].index;
                        break;
                    }
                }
                const draggedIndex = orderedTabs.findIndex(
                    (tab) => tab.orderKey === draggedIcon,
                );
                if (draggedIndex !== -1 && draggedIndex !== insertBeforeIndex) {
                    setIconOrderWithLog((prev) => {
                        const newOrder = [...prev];
                        const [removed] = newOrder.splice(draggedIndex, 1);
                        newOrder.splice(
                            insertBeforeIndex > draggedIndex
                                ? insertBeforeIndex - 1
                                : insertBeforeIndex,
                            0,
                            removed,
                        );
                        return newOrder;
                    });
                }
            } else if (draggedIcon && setFloatingWindows && setPanelGroups) {
                const tabInfo = orderedTabs.find(
                    (t) => t.orderKey === draggedIcon,
                );
                if (tabInfo) {
                    setPanelGroups((prev) =>
                        prev
                            .map((group) =>
                                group.id === tabInfo.group.id
                                    ? {
                                          ...group,
                                          tabs: group.tabs.filter(
                                              (t) => t.id !== tabInfo.tab.id,
                                          ),
                                      }
                                    : group,
                            )
                            .filter((group) => group.tabs.length > 0),
                    );
                    setFloatingWindows((prev) => [
                        ...prev,
                        {
                            id: `float-${Date.now()}-${Math.random()}`,
                            tab: tabInfo.tab,
                            position: { x: e.clientX - 100, y: e.clientY - 30 },
                            size: {
                                width: floatWindowMinWidth || 250,
                                height: floatWindowMinHeight || 180,
                            },
                            isResizing: false,
                            isDragging: false,
                        },
                    ]);
                }
            }
            document
                .querySelectorAll(
                    '.panel-tab.drop-target-left, .panel-tab.drop-target-right',
                )
                .forEach((el) => {
                    el.classList.remove(
                        'drop-target-left',
                        'drop-target-right',
                    );
                });
            document
                .querySelectorAll('.group-drop-zone.active')
                .forEach((zone) => {
                    zone.classList.remove('active');
                });
            document
                .querySelectorAll(
                    '.tab-icon-wrapper.floating-drag-shift, .tab-icon-wrapper.shift-left, .tab-icon-wrapper.shift-right, .tab-icon-wrapper.shift-up, .tab-icon-wrapper.shift-down',
                )
                .forEach((el) => {
                    el.classList.remove(
                        'floating-drag-shift',
                        'shift-left',
                        'shift-right',
                        'shift-up',
                        'shift-down',
                    );
                });
            document.querySelectorAll('.tabs-icons').forEach((el) => {
                if ('dataset' in el) {
                    delete (el as HTMLElement).dataset.insertIndex;
                }
            });
            if (isIconDragging && draggedIcon) {
                setIsIconDraggingWithLog(false);
                setDraggedIconWithLog(null);
                setIconShiftsWithLog({});
                setDragDirectionWithLog(null);
                document.body.classList.remove('dragging-sidepanel-icon');
                document.documentElement.classList.remove(
                    'dragging-sidepanel-icon',
                );
            }
        };
        window.addEventListener('mousemove', handleGlobalMouseMove);
        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            document
                .querySelectorAll(
                    '.panel-tab.drop-target-left, .panel-tab.drop-target-right',
                )
                .forEach((el) => {
                    el.classList.remove(
                        'drop-target-left',
                        'drop-target-right',
                    );
                });
            document
                .querySelectorAll('.group-drop-zone.active')
                .forEach((zone) => {
                    zone.classList.remove('active');
                });
            document
                .querySelectorAll(
                    '.tab-icon-wrapper.floating-drag-shift, .tab-icon-wrapper.shift-left, .tab-icon-wrapper.shift-right, .tab-icon-wrapper.shift-up, .tab-icon-wrapper.shift-down',
                )
                .forEach((el) => {
                    el.classList.remove(
                        'floating-drag-shift',
                        'shift-left',
                        'shift-right',
                        'shift-up',
                        'shift-down',
                    );
                });
            document.querySelectorAll('.tabs-icons').forEach((el) => {
                if ('dataset' in el) {
                    delete (el as HTMLElement).dataset.insertIndex;
                }
            });
            document.body.classList.remove('dragging-sidepanel-icon');
            document.documentElement.classList.remove(
                'dragging-sidepanel-icon',
            );
        };
    }, [
        isIconDragging,
        draggedIcon,
        handleTabDrop,
        handleDropOnZone,
        setTabDropPosition,
        orderedTabs,
        setFloatingWindowsExternal,
        panelId,
    ]);
    useEffect(() => {
        function handleTransferTabBetweenPanels(e: Event) {
            try {
                const customEvent = e as CustomEvent;
                const { tab, sourcePanel, targetPanel, insertIndex } =
                    customEvent.detail || {};
                console.log(
                    '[MinimizedSidePanel][handleTransferTabBetweenPanels] EVENT FIRED',
                    { tab, sourcePanel, targetPanel, insertIndex, panelId },
                );
                if (String(targetPanel) === String(panelId)) {
                    console.log(
                        '[MinimizedSidePanel][handleTransferTabBetweenPanels] This is the TARGET panel',
                        { panelId },
                    );
                    // Hedef paneldeki groupId'yi bul veya oluştur
                    const groupId: string =
                        (visibleGroups && visibleGroups.length > 0 && visibleGroups[0].id) ||
                        tab.groupId ||
                        (tab.group && tab.group.id) ||
                        tab.group_id ||
                        `group-${Math.random().toString(36).slice(2, 10)}`;
                    setPanelGroups?.((prevGroups) => {
                        let groups = [...prevGroups];
                        let groupIdx = groups.findIndex(
                            (g) => g.id === groupId,
                        );
                        if (groupIdx === -1) {
                            // Mevcut bir group referansı bul
                            const refGroup = prevGroups[0] || {
                                id: groupId,
                                tabs: [],
                                isVisible: true,
                                height: 200,
                            };
                            groups.push({
                                ...refGroup,
                                id: groupId,
                                tabs: [tab],
                            });
                        } else {
                            if (
                                !groups[groupIdx].tabs.some(
                                    (t) => t.id === tab.id,
                                )
                            ) {
                                groups[groupIdx] = {
                                    ...groups[groupIdx],
                                    tabs: [...groups[groupIdx].tabs, tab],
                                };
                            }
                        }
                        return groups;
                    });
                    setTimeout(() => {
                        const tabOrderKey = `${groupId}::${tab.id}`;
                        setIconOrder((prev) => {
                            let newOrder = prev.filter(
                                (key) => key !== tabOrderKey,
                            );
                            let idx =
                                typeof insertIndex === 'number'
                                    ? insertIndex
                                    : newOrder.length;
                            if (idx < 0) idx = 0;
                            if (idx > newOrder.length) idx = newOrder.length;
                            newOrder.splice(idx, 0, tabOrderKey);
                            console.log(
                                '[MinimizedSidePanel][setIconOrder] before',
                                prev,
                                'after remove',
                                newOrder,
                                'insert at',
                                idx,
                            );
                            return newOrder;
                        });
                    }, 0);
                } else {
                    // KAYNAK PANEL KONTROLÜ YAPMADAN, HER ZAMAN TAB'I SİL
                    setPanelGroups?.((prevGroups) => {
                        try {
                            let groups = prevGroups.map((group) => ({
                                ...group,
                                tabs: group.tabs.filter((t) => t.id !== tab.id),
                            }));
                            groups = groups.filter(
                                (group) => group.tabs.length > 0,
                            );
                            console.log(
                                '[MinimizedSidePanel][handleTransferTabBetweenPanels] Tab removed from minimized panel (no sourcePanel check)',
                                { tab, groups },
                            );
                            return groups;
                        } catch (err) {
                            console.warn(
                                '[MinimizedSidePanel][HANDLER] Tab silme sırasında hata:',
                                err,
                            );
                            return prevGroups;
                        }
                    });
                    setPanelGroups?.((prevGroups) => {
                        try {
                            if (
                                prevGroups.some((group) =>
                                    group.tabs.some((t) => t.id === tab.id),
                                )
                            ) {
                                console.log(
                                    '[MinimizedSidePanel][handleTransferTabBetweenPanels] Tab already exists in this panel, skipping add',
                                    { panelId, tab },
                                );
                                return prevGroups;
                            }
                            let groups = prevGroups.map((group) => ({
                                ...group,
                                tabs: group.tabs.filter((t) => t.id !== tab.id),
                            }));
                            if (targetPanel) {
                                const groupIdx = groups.findIndex(
                                    (g) => g.id === targetPanel,
                                );
                                if (groupIdx !== -1) {
                                    const group = groups[groupIdx];
                                    let newTabs = [...group.tabs];
                                    let idx: number | undefined = undefined;
                                    if (
                                        typeof insertIndex === 'number' &&
                                        insertIndex >= 0 &&
                                        insertIndex <= newTabs.length
                                    ) {
                                        idx = insertIndex;
                                    }
                                    if (idx === undefined) {
                                        idx = newTabs.length;
                                    }
                                    newTabs.splice(idx, 0, tab);
                                    groups[groupIdx] = {
                                        ...group,
                                        tabs: newTabs,
                                        isMinimized: true,
                                    };
                                    setTimeout(() => {
                                        setPopupTab && setPopupTab(null);
                                        setPopupPosition &&
                                            setPopupPosition(null);
                                    }, 0);
                                    console.log(
                                        '[MinimizedSidePanel][handleTransferTabBetweenPanels] Tab added to group',
                                        { groupIdx, group, newTabs, groups },
                                    );
                                } else {
                                    console.log(
                                        '[MinimizedSidePanel][handleTransferTabBetweenPanels] Target group not found',
                                        { targetPanel, groups },
                                    );
                                }
                            }
                            return groups;
                        } catch (err) {
                            console.warn(
                                '[MinimizedSidePanel][HANDLER] Tab ekleme sırasında hata:',
                                err,
                            );
                            return prevGroups;
                        }
                    });
                }
                setTimeout(() => {
                    document.body.classList.remove('dragging-sidepanel-icon');
                    document.documentElement.classList.remove(
                        'dragging-sidepanel-icon',
                    );
                }, 0);
            } catch (err) {
                console.warn(
                    '[MinimizedSidePanel][HANDLER] transfer-tab-between-panels event işlenirken hata:',
                    err,
                );
            }
        }
        window.addEventListener(
            PANEL_EVENTS.transferTab,
            handleTransferTabBetweenPanels as EventListener,
        );
        return () => {
            window.removeEventListener(
                PANEL_EVENTS.transferTab,
                handleTransferTabBetweenPanels as EventListener,
            );
        };
    }, [
        setPanelGroups,
        panelRef,
        visibleGroups,
        panelId,
        setPopupTab,
        setPopupPosition,
    ]);
    useEffect(() => {
        function handleRemoveTabFromPanel(e: Event) {
            try {
                const customEvent = e as CustomEvent;
                const {
                    panelId: eventPanelId,
                    tabId,
                    groupId,
                } = customEvent.detail || {};
                if (!setPanelGroups || !panelRef.current) return;
                if (eventPanelId !== panelId) return;
                setPanelGroups((prevGroups) => {
                    try {
                        const newGroups = prevGroups
                            .map((group) =>
                                group.id === groupId
                                    ? {
                                          ...group,
                                          tabs: group.tabs.filter(
                                              (t) => t.id !== tabId,
                                          ),
                                      }
                                    : group,
                            )
                            .filter((group) => group.tabs.length > 0);
                        if (newGroups.flatMap((g) => g.tabs).length === 0) {
                            setPopupTab && setPopupTab(null);
                            setPopupPosition && setPopupPosition(null);
                        }
                        console.log(
                            '[MinimizedSidePanel] Tab silindi:',
                            tabId,
                            'Grup:',
                            groupId,
                        );
                        return newGroups;
                    } catch (err) {
                        console.warn(
                            '[MinimizedSidePanel] Tab silme sırasında hata:',
                            err,
                        );
                        return prevGroups;
                    }
                });
            } catch (err) {
                console.warn(
                    '[MinimizedSidePanel] remove-tab-from-panel event işlenirken hata:',
                    err,
                );
            }
        }
        window.addEventListener(
            PANEL_EVENTS.removeTabFromPanel,
            handleRemoveTabFromPanel as EventListener,
        );
        return () => {
            window.removeEventListener(
                PANEL_EVENTS.removeTabFromPanel,
                handleRemoveTabFromPanel as EventListener,
            );
        };
    }, [setPanelGroups, panelRef, panelId, setPopupTab, setPopupPosition]);
    useEffect(() => {
        if (panelRef.current) {
            console.log(
                '[MinimizedSidePanel] MOUNT panelId:',
                panelId,
                'data-panel-id:',
                panelRef.current.getAttribute('data-panel-id'),
            );
        }
    }, [panelId, panelRef]);
    useEffect(() => {
        if (visibleGroups.length === 0) {
            onEmpty?.();
        }
    }, [visibleGroups.length, onEmpty]);
    // The rail-adjacent edge anchors the popup: it must stay fixed during a
    // resize and offers no handle, so every handle spelling containing it is
    // dropped (corners included). An absent position behaves like a
    // vertical rail anchored on its west edge.
    const anchoredEdge =
        panelPosition === 'right'
            ? 'e'
            : panelPosition === 'top'
              ? 'n'
              : panelPosition === 'bottom'
                ? 's'
                : 'w';
    const popupResizeHandles = [
        'n',
        'ne',
        'e',
        'se',
        's',
        'sw',
        'w',
        'nw',
    ].filter((handle) => !handle.includes(anchoredEdge));
    const isHorizontalPanel =
        panelPosition === 'top' || panelPosition === 'bottom';
    return (
        <TooltipProvider>
            {snapOverlay}
            {isDraggingPanel &&
                panelDragGhostHtml &&
                dragPos &&
                dragOffset &&
                dragPanelSize && (
                    <div
                        className="side-panel-drag-ghost-html minimized"
                        style={{
                            left: dragPos.x - dragOffset.x,
                            top: dragPos.y - dragOffset.y,
                            width: dragPanelSize.width,
                            height: dragPanelSize.height,
                            position: 'fixed',
                            pointerEvents: 'none',
                            zIndex: 10000,
                        }}
                        dangerouslySetInnerHTML={{ __html: panelDragGhostHtml }}
                    />
                )}
            {!isDraggingPanel && (
                <div
                    ref={panelRef}
                    data-panel-id={panelId}
                    className={`minimized-sidebar-container side-panel${isDraggingPanel ? ' grabbing' : ''}`}
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: isHorizontalPanel ? 'row' : 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                    }}
                    onMouseDown={handlePanelLongPressMouseDown}
                >
                    <div
                        className={`tabs-icons ${isHorizontalPanel ? 'horizontal' : ''} icon-alignment-${iconAlignment}`}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragLeave={handleDragLeave}
                    >
                        {orderedTabs.map(({ group, tab, refKey, orderKey }) => {
                            const isActive =
                                popupTab &&
                                popupTab.tabId === tab.id &&
                                popupTab.groupId === group.id;
                            const isDragging =
                                isIconDragging && draggedIcon === orderKey;
                            const isDragOver =
                                isIconDragging &&
                                dragOverIcon === orderKey &&
                                draggedIcon !== orderKey;
                            const shiftDirection = iconShifts[orderKey];
                            let shiftClass = '';
                            if (shiftDirection === 'left')
                                shiftClass = 'shift-left';
                            else if (shiftDirection === 'right')
                                shiftClass = 'shift-right';
                            else if (shiftDirection === 'up')
                                shiftClass = 'shift-up';
                            else if (shiftDirection === 'down')
                                shiftClass = 'shift-down';
                            const uniqueKey = `${tab.id}-${group.id}`;
                            return (
                                <Tooltip key={uniqueKey}>
                                    <TooltipTrigger asChild>
                                        <div
                                            ref={(el) => {
                                                iconRefs.current[refKey] = el;
                                            }}
                                            data-tab-icon="true"
                                            className={[
                                                'tab-icon-wrapper',
                                                isActive && 'active',
                                                isDragging && 'dragging',
                                                isDragOver && 'drag-over',
                                                shiftClass,
                                                disableTransitions &&
                                                    'no-transition',
                                                isIconDragging &&
                                                draggedIcon === orderKey
                                                    ? 'grabbing'
                                                    : 'grab',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            onMouseDown={(e) =>
                                                handleIconMouseDown(
                                                    e,
                                                    orderKey,
                                                    tab.icon,
                                                )
                                            }
                                            draggable={false}
                                        >
                                            {tab.icon ?? (
                                                <PanelLeft size={18} />
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent
                                        side={
                                            isHorizontalPanel
                                                ? 'bottom'
                                                : 'right'
                                        }
                                        className="tooltip-content"
                                    >
                                        {tab.labelKey
                                            ? t(tab.labelKey, tab.label)
                                            : tab.label}
                                    </TooltipContent>
                                </Tooltip>
                            );
                        })}
                        {popupTab &&
                            popupPosition &&
                            (() => {
                                const group = visibleGroups.find(
                                    (g) => g.id === popupTab.groupId,
                                );
                                const tab = group?.tabs.find(
                                    (t) => t.id === popupTab.tabId,
                                );
                                if (!tab || !group) return null;
                                const popupHeight = getPopupHeight(
                                    tab.id,
                                    group.id,
                                );
                                // Portal to the document body: rail ancestors
                                // (the horizontal top/bottom .panel-content
                                // carries a z-index) build stacking contexts
                                // that trap this fixed surface below the
                                // canvas overlays; a root-level portal always
                                // paints it above the workspace.
                                return createPortal(
                                    <div
                                        style={{
                                            width: popupWidth,
                                            height: popupHeight,
                                            position: 'fixed',
                                            left: popupPosition.left,
                                            top: popupPosition.top,
                                            zIndex: 1000,
                                        }}
                                        className="sidepanel-popup-wrapper"
                                    >
                                        <div
                                            ref={(el) => {
                                                popupRef.current = el;
                                            }}
                                            className="sidepanel-popup sidepanel-popup-content"
                                            onMouseDown={(e) => {
                                                if (e.button === 0) {
                                                    popupPressGuardRef.current =
                                                        true;
                                                }
                                            }}
                                            onClick={(e) => {
                                                popupPressGuardRef.current =
                                                    false;
                                                e.stopPropagation();
                                            }}
                                            style={{
                                                width: popupWidth,
                                                height: popupHeight,
                                                position: 'relative',
                                            }}
                                        >
                                            <div className="sidepanel-popup-header">
                                                <span>
                                                    {tab.labelKey
                                                        ? t(
                                                              tab.labelKey,
                                                              tab.label,
                                                          )
                                                        : tab.label}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="sidepanel-popup-pin-btn"
                                                    aria-pressed={pinned}
                                                    aria-label={
                                                        pinned
                                                            ? t(
                                                                  'shell.panelFrame.unpin',
                                                              )
                                                            : t(
                                                                  'shell.panelFrame.pin',
                                                              )
                                                    }
                                                    title={
                                                        pinned
                                                            ? t(
                                                                  'shell.panelFrame.unpin',
                                                              )
                                                            : t(
                                                                  'shell.panelFrame.pin',
                                                              )
                                                    }
                                                    // Toggle on press: a
                                                    // press that slips off the
                                                    // button before release
                                                    // retargets the click to
                                                    // an ancestor, which
                                                    // would swallow a
                                                    // click-based toggle.
                                                    onMouseDown={(e) => {
                                                        if (e.button !== 0)
                                                            return;
                                                        popupPressGuardRef.current =
                                                            true;
                                                        e.stopPropagation();
                                                        setPinnedState(
                                                            (p) => !p,
                                                        );
                                                    }}
                                                    onClick={(e) => {
                                                        popupPressGuardRef.current =
                                                            false;
                                                        e.stopPropagation();
                                                        // Pointer presses
                                                        // already toggled on
                                                        // mousedown; detail 0
                                                        // is keyboard
                                                        // activation.
                                                        if (e.detail !== 0)
                                                            return;
                                                        setPinnedState(
                                                            (p) => !p,
                                                        );
                                                    }}
                                                >
                                                    {pinned ? (
                                                        <Pin size={16} />
                                                    ) : (
                                                        <PinOff size={16} />
                                                    )}
                                                </button>
                                            </div>
                                            <div className="sidepanel-popup-body">
                                                {tab.content}
                                            </div>
                                            {popupResizeHandles.map(
                                                (handle) => (
                                                    <div
                                                        key={handle}
                                                        className={`sidepanel-popup-resize-handle sidepanel-popup-resize-handle-${handle}`}
                                                        onMouseDown={(e) =>
                                                            handlePopupResizeStart(
                                                                e,
                                                                handle,
                                                            )
                                                        }
                                                    />
                                                ),
                                            )}
                                        </div>
                                    </div>,
                                    document.body,
                                );
                            })()}
                    </div>
                    <div className="minimized-sidebar-remaining" />
                </div>
            )}
        </TooltipProvider>
    );
};
